import produce from 'immer';
import createCachedSelector from 're-reselect';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { parseMsDate, parseStringDate } from '../../api';
import { createArrayCache, flatten } from '../../data-utils';
import { Agent, CallState, DeviceGroupType, EntityType, Extension, ExtensionDetails, getKey, getQueueOrSquadKey, Identifier, isSquadId, Media, MonitorGroup, Presence, QueueOrSquadId, sameIdentifier } from '../../model';
import { isDefined } from '../../type-utils';
import { Actions, StoreState } from '../types';
import { selectConfigProp, selectFavExtensionFolderId } from './config';
import { selectAllMedia } from './media';

interface ExtensionData {
  [agent: string]: Extension | undefined
}

function extensionInfo(state: ExtensionData = {}, action: Actions): ExtensionData {
  if (action.type === getType(actions.fetchQueueAgents.response)) {
    const newState = { ...state }
    for (const agent of action.payload.agents) {
      const key = getKey(agent)
      newState[key] = { ...newState[key], ...agent }
    }
    return newState
  }

  if (action.type === getType(actions.setPresence)) {
    const { extension, presence, type, breakCode } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        presence,
        deviceGroupType: type === undefined ? info.deviceGroupType : type,
        breakCode: breakCode === undefined ? info.breakCode : breakCode,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionPresence)) {
    const { extension, presence, breakCode, deviceGroupType, returnTime, startTime } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        presence,
        breakCode,
        deviceGroupType,
        returnTime,
        startTime,
      },
    }
  }

  if (action.type === getType(actions.updateStaticData)) {
    const { id, firstName, lastName, regionId } = action.payload

    const key = getKey(id)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        name: firstName + ' ' + lastName,
        regionId,
      },
    }
  }


  if (action.type === getType(actions.setConfig)) {

    return produce(state, state => {
      const { serverName, extension, currentPresence, fullName, currentRegionReturnTime, presenceStartTime,
        currentStatus, currentSubStatus, currentAwayFromDesk, waitingForWrapup, currentDeviceGroupType,
        currentBreakcode, extendedAvailablePresence, shortcuts,
        department, regionId, expertise, operatorNotes, activity } = action.payload
      const ext = { server: serverName, id: extension }
      const key = getKey(ext)

      state[key] = {
        id: extension,
        server: serverName,
        name: fullName,
        presence: currentPresence,
        returnTime: parseMsDate(currentRegionReturnTime),
        startTime: parseMsDate(presenceStartTime),
        status: currentStatus,
        subStatus: currentSubStatus,
        awayFromDesk: currentAwayFromDesk,
        waitingForWrapup: waitingForWrapup,
        deviceGroupType: currentDeviceGroupType,
        breakCode: currentBreakcode,
        extendedAvailablePresence,
        tempLogout: false, //set by broadcast
        department,
        regionId,
        expertise,
        operatorNotes,
        activity: activity === '' ? [] : activity.split(',').filter((x: string) => x !== ''),
      }

      for (let index = 0; index < shortcuts.length; index++) {
        const button = shortcuts[index];
        if (button.entityType === EntityType.Extension && button.data) {
          const ext = { server: button.serverName, id: button.entityId }
          const key = getKey(ext)
          state[key] = {
            id: button.entityId,
            server: button.serverName,
            name: button.description,
            presence: parseInt(button.data[0]),
            returnTime: button.data[1] !== '' ? parseStringDate(button.data[1]) : undefined,
            status: parseInt(button.data[2]),
            subStatus: parseInt(button.data[3]),
            awayFromDesk: button.data[4] === '1',
            waitingForWrapup: button.data[5] === '1',
            deviceGroupType: parseInt(button.data[6]),
            breakCode: button.data[7] !== '' ? parseInt(button.data[7]) : 0,
            extendedAvailablePresence: button.data[8] === '1',
            tempLogout: button.data[9] === '1',
            department: button.data[10],
            regionId: button.data[11],
            expertise: button.data[12],
            operatorNotes: button.data[13],
            activity: button.data[14] === '' ? [] : button.data[14].split(','),
          }
        }
      }
    })
  }

  if (action.type === getType(actions.fetchExtensionsResponse) ||
    action.type === getType(actions.fetchExtensionsByIdsResponse)) {
    return produce(state, state => {
      const { extensions } = action.payload
      for (let index = 0; index < extensions.length; index++) {
        const extension = extensions[index];
        const ext = { server: extension.server, id: extension.id }
        const key = getKey(ext)
        state[key] = extension
      }
    })
  }

  if (action.type === getType(actions.updateExtensionStatus)) {
    const { extension, status, subStatus } = action.payload
    const key = getKey(extension)
    const info = state[key]

    if (!info) { return state }
    if (info.status === status && info.subStatus === subStatus) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        status,
        subStatus,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionAwayFromDesk)) {
    const { extension, awayFromDesk } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        awayFromDesk,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionTempLogout)) {
    const { extension, tempLogout } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        tempLogout,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionWaitingForWrapup)) {
    const { extension, waitingForWrapup } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        waitingForWrapup,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionDeviceGroupType)) {
    const { extension, deviceGroupType } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        deviceGroupType,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionTable)) {
    const { id, imageDate } = action.payload
    const key = getKey(id)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        imageImageDate: imageDate,
      },
    }
  }

  if (action.type === getType(actions.updateExtensionAddActivity)) {
    const { extension, activity } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        activity: [...info.activity.filter(x => x !== activity), activity],
      },
    }
  }

  if (action.type === getType(actions.updateExtensionRemoveActivity)) {
    const { extension, activity } = action.payload
    const key = getKey(extension)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        activity: [...info.activity.filter(x => x !== activity)],
      },
    }
  }

  if (action.type === getType(actions.ccRequestMonitorDesktop)) {
    const { id } = action.payload
    const key = getKey(id)
    const info = state[key]
    if (!info) { return state }

    return {
      ...state,
      [key]: {
        ...info,
        monitorDesk: info.monitorDesk !== undefined ? !info.monitorDesk : true,
      },
    }
  }

  return state
}

interface AgentStats extends Identifier {
  answered: number
  talkTime: number
  enabled: boolean
  access: number
  skill: number
  entryTime: number
  entryCount: number
}

type QueueAgents = AgentStats[] | null | undefined

interface QueueAgentsByQueue {
  [queue: string]: QueueAgents
}

function getAgentStats(agent: Agent): AgentStats {
  const { server, id, answered, talkTime, enabled, access, skill, entryTime, entryCount } = agent;
  return { server, id, answered, talkTime, enabled, access, skill, entryTime, entryCount }
}

function queueAgentStats(state: QueueAgentsByQueue = {}, action: Actions): QueueAgentsByQueue {
  if (action.type === getType(actions.fetchQueueAgents.request)) {
    const key = getKey(action.payload)
    return { ...state, [key]: state[key] || null }
  }

  if (action.type === getType(actions.fetchQueueAgents.response)) {
    const { queue, agents } = action.payload
    return { ...state, [getKey(queue)]: agents.map(getAgentStats) }
  }

  if (action.type === getType(actions.updateAgentStats)) {
    const { agent, answered, talkTime } = action.payload
    const key = getKey({ server: agent.server, id: agent.queue })
    const agents = state[key]
    if (agent.agent === null) return state

    if (agents) {
      return {
        ...state,
        [key]: agents.map(a => a.id === agent.agent ? { ...a, answered, talkTime } : a),
      }
    }
  }

  if (action.type === getType(actions.updateAgentInfo)) {
    const { agent, enabled, access, skill, entryTime, entryCount } = action.payload
    const key = getKey({ server: agent.server, id: agent.queue })
    const agents = state[key]
    if (agent.agent === null) return state

    if (agents) {
      return {
        ...state,
        [key]: !agents.find(a => a.id === agent.agent) ? [...agents, { server: agent.server, id: agent.agent, answered: 0, talkTime: 0, enabled, access, skill, entryTime, entryCount }] : agents.map(a => a.id === agent.agent ? { ...a, enabled, access, skill, entryTime, entryCount } : a),
      }
    }
  }

  if (action.type === getType(actions.removeAgentInfo)) {
    const { agent } = action.payload
    const key = getKey({ server: agent.server, id: agent.queue })
    const agents = state[key]
    if (agent.agent === null) return state

    if (agents) {
      return {
        ...state,
        [key]: agents.filter(a => a.id !== agent.agent),
      }
    }
  }

  return state
}

//really this is extension details
type QueueAgentDetails = ExtensionDetails | undefined

interface AgentByDetails {
  [queue: string]: QueueAgentDetails
}

function getQueueAgentDetails(agentDetails: ExtensionDetails): ExtensionDetails {
  const { associateNumber, mobileNumber, homeNumber, otherNumber, email, officeNumber, monitor, canReceiveVoicemail, isHomeNumberVisible, isMobileNumberVisible, isOtherNumberVisible } = agentDetails;
  return { associateNumber, mobileNumber, homeNumber, otherNumber, email, officeNumber, monitor, canReceiveVoicemail, isHomeNumberVisible, isMobileNumberVisible, isOtherNumberVisible }
}


function queueAgentDetails(state: AgentByDetails = {}, action: Actions): AgentByDetails {
  if (action.type === getType(actions.fetchExtensionDetails.response)) {
    const { id, details } = action.payload
    return { ...state, [getKey(id)]: getQueueAgentDetails(details) }
  }

  if (action.type === getType(actions.updateExtensionTable)) {
    const { id, email, mobileNumberVisible, homeNumberVisible, otherNumberVisible } = action.payload
    const data = state[getKey(id)]
    if (data)
      return { ...state, [getKey(id)]: { ...data, email, isMobileNumberVisible: mobileNumberVisible, isHomeNumberVisible: homeNumberVisible, isOtherNumberVisible: otherNumberVisible } }
  }

  if (action.type === getType(actions.updateExtensionDetails)) {
    const { id, homeNumber, mobile, office, otherNumber, associatedNumber } = action.payload
    const data = state[getKey(id)]
    if (data)
      return { ...state, [getKey(id)]: { ...data, homeNumber: homeNumber, mobileNumber: mobile, officeNumber: office, otherNumber: otherNumber, associateNumber: associatedNumber } }
  }

  if (action.type === getType(actions.updateStaticData)) {
    const { id, office, other, mobile, home, associateNumber, email } = action.payload

    const data = state[getKey(id)]
    if (data)
      return { ...state, [getKey(id)]: { ...data, homeNumber: home, mobileNumber: mobile, officeNumber: office, otherNumber: other, associateNumber: associateNumber, email } }

  }

  return state
}

interface ExtensionByRegion {
  [regionId: string]: string[] | undefined
}

function regionExtensions(state: ExtensionByRegion = {}, action: Actions): ExtensionByRegion {
  if (action.type === getType(actions.updateStaticData)) {
    return produce(state, state => {
      const { regionId, id, showInDirectory } = action.payload
      const key = getKey(id)

      //check regions 
      let region = state[regionId]
      if (region) {
        const index = region.findIndex(x => x === key)
        if (showInDirectory && index === -1) {
          delete state[regionId]
        } else if (!showInDirectory && index > -1) {
          delete state[regionId]
        }
      }

      //check entire network region
      region = state['*']
      if (region) {
        const index = region.findIndex(x => x === key)
        if (showInDirectory && index === -1) {
          delete state['*']
        } else if (!showInDirectory && index > -1) {
          delete state['*']
        }
      }

      //delete from old region that it was in 
      for (const regionKey in state) {
        if (Object.prototype.hasOwnProperty.call(state, regionKey)) {
          if (regionKey !== '*') {
            const region = state[regionKey];
            if (region) {
              const index = region.findIndex(x => x === key)
              if (regionKey !== regionId && index > -1) {
                delete state[regionKey]
              } else if (regionKey === regionId && index === -1) {
                delete state[regionKey]
              }
            }
          }
        }
      }
    })
  }

  if (action.type === getType(actions.fetchExtensionsResponse)) {
    return produce(state, state => {
      const { regionId, extensions } = action.payload
      const data = !state[regionId] ? [] : state[regionId]
      if (!data) return

      let keys: string[] = []
      for (let index = 0; index < extensions.length; index++) {
        const extension = extensions[index];
        const ext = { server: extension.server, id: extension.id }
        keys = [...keys, getKey(ext)]

      }
      state[regionId] = [...data, ...keys]
    })
  }

  if (action.type === getType(actions.favouriteExtensionAction)) {
    return produce(state, state => {
      const { add, extension, folderId } = action.payload
      const data = state[folderId]
      if (data) {
        const key = getKey(extension)
        if (add) {
          if (!data.find(k => k === key)) {
            state[folderId] = [...data, key]
          }
        }
        else {
          state[folderId] = data.filter(d => d !== key)
        }
      }
    })
  }

  if (action.type === getType(actions.forceReload)) {
    return {}
  }

  return state
}


export type SelectedExtension = Readonly<{
  id: Identifier,
  edit?: boolean,
  queue?: Identifier,
}>

function selectedExtension(state: SelectedExtension | null = null, action: Actions): SelectedExtension | null {
  if (action.type === getType(actions.updateSelectedExtension)) {
    if (!action.payload.extension) {
      state = null
    } else {
      state = {
        id: action.payload.extension,
        edit: action.payload.edit,
        queue: action.payload.queue,
      }
    }
  }

  if (action.type === getType(actions.setDirectoryView)) {
    return null
  }

  return state
}

function getAgent(info: Extension, stats: AgentStats, media: Media[] | null | undefined, agentDetails: ExtensionDetails | undefined): Agent | undefined {
  if (!('presence' in info)) return
  return { ...info, ...stats, ...agentDetails, media: media || [] }
}

export const getQueueAgents = (state: StoreState) => state.queueAgents


export const getExtensionInfos = (state: StoreState) => getQueueAgents(state).extensionInfo
const getAgents = (state: StoreState, queue: QueueOrSquadId) => getQueueAgents(state).queueAgentStats[getKey(queue)]
const getAgentDetails = (state: StoreState) => getQueueAgents(state).queueAgentDetails
const getRegionExtensions = (state: StoreState) => getQueueAgents(state).regionExtensions

export const selectQueueAgentsAreLoading = (state: StoreState, queue: QueueOrSquadId) => getAgents(state, queue) === null
export const shouldFetchQueueAgents = (state: StoreState, queue: QueueOrSquadId) => getAgents(state, queue) === undefined
export const shouldFetchExtensionDetails = (state: StoreState, agent: Identifier) => getAgentDetails(state)[getKey(agent)] === undefined

export function getSelectedExtension(state: StoreState) { return getQueueAgents(state).selectedExtension }


function doesMediaBelongToAgent(media: Media, queueOrSquadId: QueueOrSquadId, agent: Identifier) {
  return media.server === agent.server && (isSquadId(queueOrSquadId) || media.queueId === queueOrSquadId.id) && (
    media.extensionId === agent.id || media.extensionsWaitingForWrapup.includes(agent.id)
  )
}

export const selectExtensionDetails = (state: StoreState, extension: Identifier) => getAgentDetails(state)[getKey(extension)]

export const selectFavExtensions = createSelector(
  selectFavExtensionFolderId,
  getRegionExtensions,
  (folderId, extensions) => {
    if (!extensions || !folderId) return undefined
    const data = extensions[folderId]
    if (!data) return undefined
    return data
  },
)

const agentMediaCache = createArrayCache<Media>()

type QueueOrSquadAgent = { agent: AgentStats, queueOrSquadId: QueueOrSquadId }

function getQueueOrSquadAgentKey({ agent, queueOrSquadId }: QueueOrSquadAgent) {
  return getQueueOrSquadKey(queueOrSquadId) + '*' + getKey(agent)
}

const selectQueueOrSquadAgent = createCachedSelector(
  (store, { agent }) => getAgentDetails(store)[getKey(agent)],
  (store, { agent }) => getExtensionInfos(store)[getKey(agent)],
  (store: StoreState, { agent }: QueueOrSquadAgent) => agent,
  (store, { agent, queueOrSquadId }) => {
    const media = selectAllMedia(store)
    const agentMedia = media.filter(m => doesMediaBelongToAgent(m, queueOrSquadId, agent)).map(m => {
      //this is not perfect as we don't have the devices of the agent
      if (m.isQueueCall && m.status !== CallState.WaitingForWrapup && !(m.extensionId === agent.id || m.deviceId === agent.id) && m.extensionsWaitingForWrapup.some(x => x === agent.id)) {
        return { ...m, status: CallState.WaitingForWrapup }
      } else {
        return m
      }
    })
    return agentMediaCache(agentMedia, getQueueOrSquadAgentKey({ agent, queueOrSquadId }))
  },
  (details, ex, agent, media) => {
    return getAgent(ex!, agent, media, details)
  },
)(
  (store, key) => getQueueOrSquadAgentKey(key),
)

const queueAgentMediaCache = createArrayCache<Agent>()

export function selectQueueAgents(state: StoreState, queueOrSquadId: QueueOrSquadId) {
  return queueAgentMediaCache(
    (getAgents(state, queueOrSquadId) || []).map(agent => selectQueueOrSquadAgent(state, { agent, queueOrSquadId })).filter(isDefined),
    getQueueOrSquadKey(queueOrSquadId),
  )
}
const queueMediaCache = createArrayCache<Media>()

export const selectQueueMedia = createCachedSelector(
  selectAllMedia,
  (s: StoreState, key: QueueOrSquadId) => isSquadId(key) ? selectQueueAgents(s, key) : null,
  (_, key) => key,
  (media, fromAgents, id) => {
    const result = fromAgents
      ? flatten(fromAgents.map(x => x.media.filter(m => m.status !== CallState.None)))
      : media.filter(isDefined).filter(m => m.status !== CallState.None && sameIdentifier({ server: m.server, id: m.queueId }, id))

    return queueMediaCache(result, getQueueOrSquadKey(id))
  },
)(
  (_, key) => getQueueOrSquadKey(key),
)

export function selectQueueAgent(state: StoreState, pars: { queue: QueueOrSquadId, ext: string }) {
  const allAgents = selectQueueAgents(state, pars.queue)
  return allAgents.filter(a => a.id === pars.ext)[0]
}

export function selectExtension(state: StoreState, extension: Identifier) {
  return getExtensionInfos(state)[getKey(extension)]
}


export default combineReducers({
  extensionInfo,
  queueAgentStats,
  queueAgentDetails,
  regionExtensions,
  selectedExtension,
})

const regionExtensionsCache = createArrayCache<Extension>()

export const selectRegionExtenions = createCachedSelector(
  getRegionExtensions,
  (state: StoreState, regionId: string) => {
    const region = getRegionExtensions(state)
    const extensions = getExtensionInfos(state)
    const k = region[regionId]
    let data: Extension[] = []
    if (k && extensions) {
      k.forEach(key => {
        const e = extensions[key]
        if (e) {
          data = [...data, e]
        }
      })
    }
    return regionExtensionsCache(data, regionId)
  },
  (s: StoreState, regionId: string) => regionId,
  (keys, data, regionId) => {
    if (!keys[regionId] || !data) return undefined
    return data
  })((s: StoreState, regionId: string) => regionId)

export const selectPresence = createCachedSelector(
  (store, id: Identifier | null) => id && getExtensionInfos(store)[getKey(id)],
  (s: StoreState, id: Identifier | null) => id && id.server,
  (s: StoreState, id: Identifier | null) => id && id.id,
  (info, server, id) => {
    if (id !== null && server !== null) {
      const agent = info
      if (agent && 'presence' in agent) {
        return {
          presence: agent.presence,
          type: agent.deviceGroupType,
          breakCode: agent.breakCode,
          operatorNotes: agent.operatorNotes,
          returnTime: agent.returnTime,
          regionId: agent.regionId,
        }
      }
    }
    return {
      presence: Presence.Available,
      type: DeviceGroupType.None,
    }
  },
)((s, id) => id && getKey(id))

const monitorGroupCache = createArrayCache<MonitorGroup>()

export enum MonitorDesktopState {
  disabled = 0,
  enabled = 1,
  running = 3,
}

export const selectDesktopMonitorExtension = createCachedSelector(
  (s: StoreState) => {
    const x = selectConfigProp(s, 'monitorGroups')
    return monitorGroupCache(x ? x : [], 'key')
  },
  (s: StoreState, id: Identifier) => selectExtension(s, id),
  (s: StoreState, id: Identifier) => id,
  (monitor, extension, id) => {
    if (!extension) return MonitorDesktopState.disabled

    const group = monitor.find(_ => _.extensionId === id.id)
    if (!group) return MonitorDesktopState.disabled

    return extension.monitorDesk === true ? MonitorDesktopState.running : MonitorDesktopState.enabled
  },
)(
  (_, id) => `{id.id}-{id.server}`,
)
